/**
 * TenantAuditTab.tsx — F7a T7.8.
 *
 * Zakładka "Audit" w AdminTenantDetail. Audit log żyje w tenant DB — endpoint
 * obsługuje `dbUnavailable` dla soft-delete / provisioning.
 *
 * Filtrowanie „jak w katalogu" — całe w nagłówkach kolumn (client-side): szukajka pod
 * User/Akcja/Encja/Szczegóły/IP, zakres dat od/do pod kolumną „Data". Nazwy akcji
 * humanizowane (surowy kod jako subtekst). Data: GET /api/admin/tenants/:id/audit-log?limit=100.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';
import { humanizeAuditAction } from '@/lib/humanize-audit-action';

interface AuditRow {
  id: string;
  userId: string | null;
  userEmail: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  details: unknown;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

interface AuditResponse {
  tenant: { id: string; slug: string; code: string };
  entries: AuditRow[];
  total: number;
  limit: number;
  offset: number;
  dbUnavailable: boolean;
  dbUnavailableReason?: string;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export interface TenantAuditTabProps {
  tenantId: string;
}

export function TenantAuditTab({ tenantId }: TenantAuditTabProps) {
  const { t } = useTranslation();

  // Filtrowanie „jak w katalogu" — całe w nagłówkach kolumn (client-side na pobranej
  // liście). Pobieramy do górnego limitu, żeby filtry per kolumna obejmowały listę.
  const query = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'auditLog'],
    queryFn: async (): Promise<AuditResponse> => {
      const res = await api.get<AuditResponse>(`/admin/tenants/${tenantId}/audit-log?limit=100`);
      return res.data;
    },
  });

  const columns = useMemo<ColumnDefinition<AuditRow>[]>(
    () => [
      {
        id: 'createdAt',
        // Data-only (YYYY-MM-DD) do porównania zakresu; renderCell pokazuje datę z godziną.
        accessor: (row) => (row.createdAt ? row.createdAt.slice(0, 10) : ''),
        header: t('admin.tenants.detail.auditLog.columns.createdAt'),
        sortable: true,
        // Zakres dat od/do pod kolumną (zamiast osobnego paska nad tabelą).
        headerFilter: { category: 'date' },
        renderCell: (_value, row) => (
          <span className="font-mono text-xs text-text-secondary">
            {formatDateTime(row.createdAt)}
          </span>
        ),
      },
      {
        id: 'user',
        accessor: (row) => row.userEmail ?? '',
        header: t('admin.tenants.detail.auditLog.columns.user'),
        sortable: true,
        renderCell: (_value, row) =>
          row.userEmail ? (
            <span className="text-sm text-text-primary">{row.userEmail}</span>
          ) : (
            <span className="text-xs italic text-text-muted">
              {t('admin.tenants.detail.auditLog.noUser')}
            </span>
          ),
      },
      {
        id: 'action',
        accessor: (row) => row.action,
        header: t('admin.tenants.detail.auditLog.columns.action'),
        sortable: true,
        // Szukajka po surowym kodzie akcji (np. „tenant.") — filtr pod kolumną.
        // Ludzka nazwa akcji („Klient · zawieszono") + surowy kod pod spodem.
        renderCell: (_value, row) => {
          const { label } = humanizeAuditAction(row.action, t);
          return (
            <div className="flex flex-col">
              <span className="text-xs text-text-primary">{label}</span>
              <span className="font-mono text-[11px] text-text-tertiary">{row.action}</span>
            </div>
          );
        },
      },
      {
        id: 'entity',
        accessor: (row) => `${row.entityType ?? ''} ${row.entityId ?? ''}`.trim(),
        header: t('admin.tenants.detail.auditLog.columns.entity'),
        // Szukajka po typie/ID encji — filtr pod kolumną.
        renderCell: (_value, row) =>
          row.entityType ? (
            <div className="flex flex-col">
              <span className="text-xs text-text-primary">{row.entityType}</span>
              {row.entityId && (
                <span className="font-mono text-[11px] text-text-tertiary">{row.entityId}</span>
              )}
            </div>
          ) : (
            <span className="text-xs text-text-muted">
              {t('admin.tenants.detail.auditLog.noEntity')}
            </span>
          ),
      },
      {
        id: 'details',
        // Serializowany JSON — szukajka tekstowa po zawartości szczegółów.
        accessor: (row) =>
          row.details !== null && row.details !== undefined ? JSON.stringify(row.details) : '',
        header: t('admin.tenants.detail.auditLog.columns.details'),
        sortable: false,
        renderCell: (_value, row) =>
          row.details !== null && row.details !== undefined ? (
            <pre className="max-w-xs overflow-hidden truncate rounded bg-bg-tertiary px-2 py-1 font-mono text-[11px] text-text-secondary">
              {JSON.stringify(row.details)}
            </pre>
          ) : (
            <span className="text-xs text-text-muted">
              {t('admin.tenants.detail.auditLog.noDetails')}
            </span>
          ),
      },
      {
        id: 'ip',
        accessor: (row) => row.ip ?? '',
        header: t('admin.tenants.detail.auditLog.columns.ip'),
        renderCell: (_value, row) =>
          row.ip ? (
            <span className="font-mono text-xs text-text-secondary">{row.ip}</span>
          ) : (
            <span className="text-xs text-text-muted">
              {t('admin.tenants.detail.auditLog.noIp')}
            </span>
          ),
      },
    ],

    [t],
  );

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={48} />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
      >
        <p>{t('admin.tenants.detail.auditLog.loadError')}</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={() => void query.refetch()}>
          {t('admin.tenants.detail.auditLog.retry')}
        </Button>
      </div>
    );
  }

  if (query.data?.dbUnavailable) {
    return (
      <div
        role="status"
        className="rounded-xl border border-amber-300 bg-amber-50 p-6 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
      >
        {query.data?.dbUnavailableReason === 'TENANT_UPDATING'
          ? t('admin.tenants.dbUpdatingNotice')
          : t('admin.tenants.detail.auditLog.dbUnavailable')}
      </div>
    );
  }

  const rows = query.data?.entries ?? [];
  const emptyMessage = t('admin.tenants.detail.auditLog.empty');

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-semibold text-text-primary">
          {t('admin.tenants.detail.auditLog.title')}
        </h3>
        <p className="mt-0.5 text-xs text-text-tertiary">
          {t('admin.tenants.detail.auditLog.subtitle')}
        </p>
      </div>

      <PreviewTenantAuthBoundary>
        <ConfigurableTable<AuditRow>
          tableId="admin-tenant-audit"
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          persistConfig={false}
          showViewModeToggle={false}
          emptyIcon={History}
          emptyTitle={emptyMessage}
          emptyDescription=""
        />
      </PreviewTenantAuthBoundary>
    </div>
  );
}
